import React, { FC, forwardRef, PropsWithChildren, useState } from 'react';
import css from './styles/feedback.scss';
import { Button } from 'shamrock-clover-ui/dist/clover/components/Button/Button';
import { Dialog } from 'shamrock-clover-ui/dist/clover/components/Dialog/Dialog';
import { useSelector } from 'react-redux';
import Slide from '@mui/material/Slide';
import Portal from '@mui/material/Portal';
import { Snackbar, Variant } from 'shamrock-clover-ui/dist/clover/components/Snackbar/Snackbar';
import { useTranslation } from 'react-i18next';
import { ChatBubbleOutline } from '@mui/icons-material';
import LoadingSpinner from '../../components/core/loading-spinner';
import classNames from 'classnames';
import feedbackService from '../../services/feedback.service';
import { RootState } from 'bootstrap';
import { Tooltip } from 'shamrock-clover-ui';

interface Props {
  isCollapsed: boolean;
}

const sourceMap = {
  credit: 'Credit',
  freight: 'Freight',
  fuel: 'Fuel',
  factoring: 'Factoring',
  loads: 'ProTransport BASIC',
  premium: 'ProTransport PREMIUM',
  enterprise: 'ProTransport ENTERPRISE',
};

const SnackWrapper = forwardRef((props: PropsWithChildren<any>, ref: any) => (
  <div ref={ref} {...props} style={{ position: 'fixed', bottom: 0, right: 0 }}>
    <Portal>{props.children}</Portal>
  </div>
));
SnackWrapper.displayName = 'SnackWrapper';

const Feedback: FC<Props> = ({ isCollapsed }) => {
  const user = useSelector((state: RootState) => state.app.user);
  const [showForm, setShowForm] = useState(false);
  const [feedback, setFeedback] = useState('');
  const snackBarDefault = { message: '', variant: '', open: false };
  const [snackbarState, setSnackbarState] = useState(snackBarDefault);
  const [loading, setLoading] = useState(false);
  const { t } = useTranslation();
  const permissions = useSelector((state: RootState) => state.app.permissions);
  const tmsPremiumUser = !!permissions?.includes('ProTransportWeb.user') || !!permissions?.includes('tms.user.premium');
  const tmsEnterpriseUser =
    !!permissions?.includes('ProTransportWeb.user.enterprise') || !!permissions?.includes('tms.user.enterprise');
  const sendFeedback = async () => {
    setLoading(true);
    let sourceKey = window.location.pathname.split('/')[1];
    if (sourceKey === 'loads') {
      if (tmsEnterpriseUser) {
        sourceKey = 'enterprise';
      } else if (tmsPremiumUser) {
        sourceKey = 'premium';
      }
    }
    const source = sourceMap[sourceKey];
    try {
      const feedbackPayload = {
        user: {
          company: user?.company,
          email: user?.email,
          phone: user?.phone,
          firstname: user?.firstname,
          lastname: user?.lastname,
        },
        message: feedback,
        source: source,
        url: window.location.href,
        type: 'FEEDBACK',
        platform: 'WEB',
      };
      await feedbackService.sendFeedback(feedbackPayload);
      setFeedback('');
      setShowForm(false);
      setSnackbarState({
        message: t('sideNavigation.feedback_submitted_successfully'),
        variant: 'success',
        open: true,
      });
    } catch (error) {
      console.log(error);
      setSnackbarState({
        message: t('sideNavigation.feedback_submission_failed'),
        variant: 'error',
        open: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const dialogContent = (
    <div className={css.dialogContent}>
      {loading && (
        <LoadingSpinner
          customStyles={{
            position: 'absolute',
            height: 'calc(100% + 174px)',
            width: 'calc(100% + 40px)',
            top: '-65px',
            left: '-20px',
            right: '-20px',
          }}
        />
      )}
      <div className={css.dialogHeader}>{t('sideNavigation.share_ideas')}</div>
      <div className={css.dialogSub}>{t('sideNavigation.we\'d_love_to_hear_your_ideas')}!</div>
      <textarea
        className={css.dialogTextarea}
        placeholder={t('sideNavigation.idea_details')}
        maxLength={1000}
        value={feedback}
        onChange={(e) => setFeedback(e.target.value)}
      />
      <div className={css.charCount}>{feedback.length}/1000</div>
    </div>
  );

  const actions = (
    <div className={css.actions}>
      <Button disabled={feedback.length < 1} onClick={sendFeedback}>
        {t('sideNavigation.share_ideas')}
      </Button>
    </div>
  );

  return (
    <div>
      <Tooltip
        title={t('sideNavigation.feedback')}
        placement="right"
        style={!isCollapsed ? { display: 'none' } : { zIndex: 9999 }}
      >
        <button
          className={classNames(css.iconContainer, isCollapsed && css.collapsed)}
          onClick={() => setShowForm(true)}
        >
          <ChatBubbleOutline fontSize="small" className={classNames(css.icon, !isCollapsed && css.iconExpanded)} />
          {!isCollapsed && <div className={css.title}>{t('sideNavigation.feedback')}</div>}
        </button>
      </Tooltip>
      <div className={css.dialogWrapper}>
        <Dialog
          open={showForm}
          content={dialogContent}
          onClose={() => setShowForm(false)}
          actions={actions}
          closeOnBackdropClick
        />
      </div>
      <Slide direction="left" in={snackbarState.open} mountOnEnter unmountOnExit>
        <SnackWrapper>
          <Snackbar
            width="400px"
            duration={5000}
            open={snackbarState.open}
            position='bottomMiddle'
            message={snackbarState.message}
            variant={snackbarState.variant as Variant}
            onClose={() => setSnackbarState(snackBarDefault)}
          />
        </SnackWrapper>
      </Slide>
    </div>
  );
};

export default Feedback;
